import React, { useState, useEffect, useRef } from 'react';
import { PersonStanding, Target, Salad, Dumbbell, AlertCircle, UploadCloud, XCircle, FolderOpen, Trash2, History, Volume2, MessageCircle, Send, CalendarDays, X, HeartPulse } from 'lucide-react';
import './App.css'; // Import the CSS file

// Firebase Imports
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc, setDoc, deleteDoc, collection, onSnapshot, serverTimestamp } from 'firebase/firestore';

// --- Firebase Configuration (IMPORTANT: PASTE YOURS HERE) ---
const firebaseConfig = {
  apiKey: "AIzaSyA2t0hQ8D-MdKx24ORXxAWdcpeGHoFdzJI",
  authDomain: "are-you-fit-cac45.firebaseapp.com",
  projectId: "are-you-fit-cac45",
  storageBucket: "are-you-fit-cac45.firebasestorage.app",
  messagingSenderId: "632789630215",
  appId: "1:632789630215:web:6a272917efbbbe702519a2",
  measurementId: "G-7EMQPVDX3F"
};
// --- END Firebase Configuration ---

// Initialize Firebase (outside of component to avoid re-initialization)
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

// Helper function to convert a File object to a Base64 string
const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            // Remove the data:image/png;base64, prefix
            const base64String = reader.result.split(',')[1];
            resolve(base64String);
        };
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(file);
    });
};

// Helper function to convert base64 to ArrayBuffer
const base64ToArrayBuffer = (base64) => {
    const binaryString = window.atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
};

// Helper function to convert PCM audio data to a WAV file Blob
const pcmToWav = (pcmData, sampleRate) => {
    const dataView = new DataView(new ArrayBuffer(44 + pcmData.byteLength));
    let offset = 0;

    // RIFF identifier
    function writeString(str) {
        for (let i = 0; i < str.length; i++) {
            dataView.setUint8(offset++, str.charCodeAt(i));
        }
    }

    // write a 32-bit integer
    function writeUint32(int) {
        dataView.setUint32(offset, int, true);
        offset += 4;
    }

    // write a 16-bit integer
    function writeUint16(int) {
        dataView.setUint16(offset, int, true);
        offset += 2;
    }

    // Write WAV header
    writeString('RIFF'); // ChunkID
    writeUint32(36 + pcmData.byteLength); // ChunkSize
    writeString('WAVE'); // Format
    writeString('fmt '); // Subchunk1ID
    writeUint32(16); // Subchunk1Size
    writeUint16(1); // AudioFormat (1=PCM)
    writeUint16(1); // NumChannels
    writeUint32(sampleRate); // SampleRate
    writeUint32(sampleRate * 2); // ByteRate (SampleRate * NumChannels * BitsPerSample/8)
    writeUint16(2); // BlockAlign (NumChannels * BitsPerSample/8)
    writeUint16(16); // BitsPerSample
    writeString('data'); // Subchunk2ID
    writeUint32(pcmData.byteLength); // Subchunk2Size

    // Write PCM data
    const pcmBytes = new Int16Array(pcmData);
    for (let i = 0; i < pcmBytes.length; i++) {
        dataView.setInt16(offset, pcmBytes[i], true);
        offset += 2;
    }

    return new Blob([dataView], { type: 'audio/wav' });
};


// Exponential backoff for API calls
const callApiWithBackoff = async (apiCall, retries = 3, delay = 1000) => {
    for (let i = 0; i < retries; i++) {
        try {
            const response = await apiCall();
            if (response.ok) {
                return response;
            } else if (response.status === 429) { // Too Many Requests
                console.warn(`Rate limit hit, retrying in ${delay / 1000}s...`);
                // eslint-disable-next-line no-loop-func
                await new Promise(res => setTimeout(res, delay)); // Suppress no-loop-func warning
                delay *= 2; // Exponential increase
            } else {
                const errorData = await response.json();
                throw new Error(`API Error: ${response.status} - ${errorData.error.message || response.statusText}`);
            }
        } catch (error) {
            if (i === retries - 1) throw error;
            console.error(`Attempt ${i + 1} failed: ${error.message}. Retrying...`);
            // eslint-disable-next-line no-loop-func
            await new Promise(res => setTimeout(res, delay)); // Suppress no-loop-func warning
            delay *= 2;
        }
    }
};

function App() {
    // eslint-disable-next-line no-unused-vars
    const [user, setUser] = useState(null);
    const [userId, setUserId] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreviewUrl, setImagePreviewUrl] = useState('');
    const [analysisResult, setAnalysisResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [isDragging, setIsDragging] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [aiTone, setAiTone] = useState('Motivational');
    const [completedWorkouts, setCompletedWorkouts] = useState({});
    const [savedAnalyses, setSavedAnalyses] = useState([]);
    const [audioUrl, setAudioUrl] = useState(null); // New state for audio
    const [isGeneratingAudio, setIsGeneratingAudio] = useState(false); // New loading state for audio
    
    // --- Chatbot States ---
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [chatMessages, setChatMessages] = useState([{ role: 'bot', text: 'Hello! I am your AI health coach. Ask me anything about fitness, diet, or general wellness.' }]);
    const [chatInput, setChatInput] = useState('');
    const [isChatLoading, setIsChatLoading] = useState(false);
    const messagesEndRef = useRef(null);

    // --- Schedule Modal States ---
    const [isScheduleOpen, setIsScheduleOpen] = useState(false);
    const [scheduleAnalysis, setScheduleAnalysis] = useState(null);
    const [selectedDayIndex, setSelectedDayIndex] = useState(0); // 0-based index for the day

    // --- User Input States ---
    const [weight, setWeight] = useState('');
    const [height, setHeight] = useState('');
    const [heightUnit, setHeightUnit] = useState('cm');
    const [age, setAge] = useState('');
    const [gender, setGender] = useState('male');

    // Scroll to bottom of chat messages
    useEffect(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, [chatMessages]);

    const handleOpenSchedule = (analysis) => {
        setScheduleAnalysis(analysis);
        setIsScheduleOpen(true);
        setSelectedDayIndex(0); // Reset to the first day when opening
    };

    const handleCloseSchedule = () => {
        setIsScheduleOpen(false);
        setScheduleAnalysis(null);
    };


    // --- Firebase Auth & Firestore Listener ---
    useEffect(() => {
        setLoadingMessage('Signing in...');
        setLoading(true);
        const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                setUser(currentUser);
                setUserId(currentUser.uid);
                setLoadingMessage('Loading your saved analyses...');
                const q = collection(db, `analyses/${currentUser.uid}/history`);
                const unsubscribeFirestore = onSnapshot(q, (snapshot) => {
                    const analyses = snapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    })).sort((a, b) => b.timestamp?.toDate() - a.timestamp?.toDate());
                    setSavedAnalyses(analyses);
                    setLoading(false);
                    setLoadingMessage('');
                }, (firestoreError) => {
                    console.error("Error fetching analyses:", firestoreError);
                    setError("Failed to load saved analyses. Please check your internet connection and Firebase rules.");
                    setLoading(false);
                    setLoadingMessage('');
                });
                return () => unsubscribeFirestore();
            } else {
                try {
                    await signInAnonymously(auth);
                } catch (anonError) {
                    console.error("Anonymous sign-in failed:", anonError);
                    setError("Failed to sign in. Please check your internet connection.");
                    setLoading(false);
                    setLoadingMessage('');
                }
            }
        });

        return () => unsubscribeAuth();
    }, []);

    // --- Image Upload Handlers ---
    const handleImageChange = (file) => {
        if (file) {
            setSelectedImage(file);
            setImagePreviewUrl(URL.createObjectURL(file));
            setAnalysisResult(null);
            setError('');
            setCompletedWorkouts({});
            setAudioUrl(null); // Clear audio
        } else {
            setSelectedImage(null);
            setImagePreviewUrl('');
            setAnalysisResult(null);
            setError('');
            setCompletedWorkouts({});
            setAudioUrl(null); // Clear audio
        }
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleDrop = (event) => {
        event.preventDefault();
        setIsDragging(false);
        const files = event.dataTransfer.files;
        if (files && files.length > 0) {
            handleImageChange(files[0]);
        }
    };

    const handleFileInputChange = (event) => {
        const file = event.target.files[0];
        handleImageChange(file);
    };

    const handleRemoveImage = () => {
        handleImageChange(null);
    };

    // --- Workout Checkbox Handler ---
    const handleToggleWorkoutCompletion = async (week, dayIndex) => {
        if (!userId || !analysisResult || !analysisResult.id) {
            setError("Cannot save workout progress without a loaded analysis.");
            return;
        }
        const key = `week-${week}-day-${dayIndex}`;
        const newCompletedWorkouts = {
            ...completedWorkouts,
            [key]: !completedWorkouts[key]
        };
        setCompletedWorkouts(newCompletedWorkouts);

        try {
            const analysisRef = doc(db, `analyses/${userId}/history`, analysisResult.id);
            await setDoc(analysisRef, {
                completedWorkouts: newCompletedWorkouts
            }, { merge: true });
        } catch (saveError) {
            console.error("Failed to save workout completion:", saveError);
            setError("Failed to save workout progress. Please try again.");
            setCompletedWorkouts(prev => ({
                ...prev,
                [key]: !prev[key]
            }));
        }
    };

    // --- AI Analysis Logic ---
    const handleAnalyzeBody = async () => {
        if (!selectedImage) {
            setError('Please upload an image first.');
            return;
        }
        if (!weight || !height || !age || !gender) {
            setError('Please provide your height, weight, age, and gender for a more accurate analysis.');
            return;
        }
        if (!userId) {
            setError('User not authenticated. Please wait or refresh.');
            return;
        }

        setLoading(true);
        setError('');
        setAnalysisResult(null);
        setLoadingMessage('Uploading image...');
        setAudioUrl(null); // Clear old audio

        try {
            const base64ImageData = await fileToBase64(selectedImage);
            setLoadingMessage('Analyzing with AI...');

            let heightInMeters;
            if (heightUnit === 'cm') {
                heightInMeters = parseFloat(height) / 100;
            } else if (heightUnit === 'feet') {
                heightInMeters = parseFloat(height) * 0.3048;
            } else {
                heightInMeters = parseFloat(height);
            }
            
            const bmiValue = parseFloat(weight) / (heightInMeters * heightInMeters);
            const bmiCategory = bmiValue < 18.5 ? "Underweight" :
                                bmiValue < 24.9 ? "Normal weight" :
                                bmiValue < 29.9 ? "Overweight" : "Obese";
            
            let prompt = `Analyze the body in this image. The user has provided the following data:
            - Age: ${age}
            - Gender: ${gender}
            - Weight: ${weight} kg
            - Height: ${height} ${heightUnit}

            Based on this information and the visual appearance of the person in the image, provide the following information in a JSON format.
            1.  **bodyType**: A general assessment of the body type (e.g., "Mesomorph", "Ectomorph", "Endomorph", "Athletic", "V-shaped", "Pear-shaped", "Apple-shaped", "Hourglass").
            2.  **fatDistributionAreas**: A list of general areas where fat distribution appears more prominent based on visible characteristics (e.g., ["Abdominal area", "Thighs", "Hips", "Arms"]).
            3.  **estimatedBMI**: The calculated BMI value and category based on the provided weight and height, e.g., "Normal (${bmiValue.toFixed(1)})".
            4.  **healthRiskAssessment**: A general, non-diagnostic health risk assessment based on the body's visual characteristics, provided data, age, and gender. Frame this in a positive and encouraging tone, e.g., "Slightly increased risk for certain health issues. Focusing on a balanced diet and regular exercise can help mitigate these risks."
            5.  **ageGenderComparison**: An assessment of whether the provided height and weight are in a typical range for the user's age and gender.
            6.  **healthyDietSuggestions**: General healthy dietary suggestions tailored to a balanced lifestyle, considering the visual assessment and provided data. Emphasize whole foods, balanced macros, and hydration. Structure this as a string with bullet points or paragraphs.
            7.  **twoWeekWorkoutPlan**: A 2-week daily workout plan in an array of objects. Each object should have 'week', 'day', 'focus' (e.g., "Full Body Strength", "Cardio & Core", "Upper Body", "Lower Body", "Active Recovery", "Rest"), and 'exercises' (an array of strings, e.g., ["Squats: 3 sets of 10 reps", "Push-ups: 3 sets to failure"]). Include warm-up and cool-down for workout days.

            If no specific fat distribution areas are prominent, state "Evenly distributed" or "Minimal visible fat".
            Ensure the response is a valid JSON object. Avoid making any medical diagnoses or highly specific measurements.`;

            if (aiTone === 'Motivational') {
                prompt += ` Ensure the tone is highly motivational and encouraging throughout.`;
            } else if (aiTone === 'Factual') {
                prompt += ` Ensure the tone is purely factual and objective.`;
            } else if (aiTone === 'Concise') {
                prompt += ` Ensure the response is very concise and to the point.`;
            }

            const payload = {
                contents: [
                    {
                        role: "user",
                        parts: [
                            { text: prompt },
                            {
                                inlineData: {
                                    mimeType: selectedImage.type,
                                    data: base64ImageData
                                }
                            }
                        ]
                    }
                ],
                generationConfig: {
                    responseMimeType: "application/json",
                    responseSchema: {
                        type: "OBJECT",
                        properties: {
                            bodyType: { type: "STRING" },
                            fatDistributionAreas: {
                                type: "ARRAY",
                                items: { type: "STRING" }
                            },
                            estimatedBMI: { type: "STRING" },
                            healthRiskAssessment: { type: "STRING" },
                            ageGenderComparison: { type: "STRING" },
                            healthyDietSuggestions: { type: "STRING" },
                            twoWeekWorkoutPlan: {
                                type: "ARRAY",
                                items: {
                                    type: "OBJECT",
                                    properties: {
                                        week: { type: "NUMBER" },
                                        day: { type: "STRING" },
                                        focus: { type: "STRING" },
                                        exercises: {
                                            type: "ARRAY",
                                            items: { type: "STRING" }
                                        }
                                    }
                                }
                            }
                        },
                        required: ["bodyType", "fatDistributionAreas", "estimatedBMI", "healthRiskAssessment", "ageGenderComparison", "healthyDietSuggestions", "twoWeekWorkoutPlan"]
                    }
                }
            };

            const googleApiKey = "AIzaSyChwqbh51eMAAak_nWG5nGs3q0CE3jIA3g";
            const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-05-20:generateContent?key=${googleApiKey}`;

            const apiCall = () => fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const response = await callApiWithBackoff(apiCall);
            const result = await response.json();

            setLoadingMessage('Processing results and saving...');

            if (result.candidates && result.candidates.length > 0 &&
                result.candidates[0].content && result.candidates[0].content.parts &&
                result.candidates[0].content.parts.length > 0) {
                const jsonText = result.candidates[0].content.parts[0].text;
                try {
                    const parsedData = JSON.parse(jsonText);
                    const analysisId = doc(collection(db, `analyses/${userId}/history`)).id;

                    const analysisToSave = {
                        ...parsedData,
                        timestamp: serverTimestamp(),
                        imageMimeType: selectedImage.type,
                        imageData: base64ImageData,
                        completedWorkouts: {},
                        userData: { weight, height, heightUnit, age, gender }
                    };

                    await setDoc(doc(db, `analyses/${userId}/history`, analysisId), analysisToSave);

                    setAnalysisResult({ ...parsedData, id: analysisId, userData: analysisToSave.userData });
                    setCompletedWorkouts({});

                } catch (jsonError) {
                    setError('Failed to parse AI response or save to database. Please try again.');
                    console.error('JSON parsing/Firestore save error:', jsonError, 'Raw text:', jsonText);
                }
            } else {
                setError('Could not get a description. The AI response was empty or malformed. Please try another image.');
                console.error('Unexpected API response structure:', result);
            }

        } catch (err) {
            console.error('Error analyzing body:', err);
            setError(`Failed to analyze image: ${err.message}. This might be a network issue or an API error. Please try again.`);
        } finally {
            setLoading(false);
            setLoadingMessage('');
        }
    };

    // --- Load/Delete Analysis from History ---
    const handleLoadSpecificAnalysis = async (analysisId) => {
        if (!userId) {
            setError("User not authenticated. Cannot load analysis.");
            return;
        }
        setLoading(true);
        setLoadingMessage('Loading analysis from history...');
        setError('');
        setAudioUrl(null); // Clear old audio when loading a new analysis
        try {
            const analysisRef = doc(db, `analyses/${userId}/history`, analysisId);
            const analysisSnap = await getDoc(analysisRef);

            if (analysisSnap.exists()) {
                const data = analysisSnap.data();
                setAnalysisResult({ ...data, id: analysisSnap.id });
                setImagePreviewUrl(`data:${data.imageMimeType};base64,${data.imageData}`);
                setSelectedImage(null);
                setCompletedWorkouts(data.completedWorkouts || {});
                setWeight(data.userData?.weight || '');
                setHeight(data.userData?.height || '');
                setHeightUnit(data.userData?.heightUnit || 'cm');
                setAge(data.userData?.age || '');
                setGender(data.userData?.gender || 'male');
                setLoadingMessage('Analysis loaded!');
            } else {
                setError("Analysis not found in history.");
            }
        } catch (loadError) {
            console.error("Error loading analysis:", loadError);
            setError("Failed to load analysis. Please try again.");
        } finally {
            setLoading(false);
            setTimeout(() => setLoadingMessage(''), 1000);
        }
    };

    const handleDeleteAnalysis = async (analysisId) => {
        if (!userId) {
            setError("User not authenticated. Cannot delete analysis.");
            return;
        }
        if (window.confirm('Are you sure you want to delete this analysis? This cannot be undone.')) {
            setLoading(true);
            setLoadingMessage('Deleting analysis...');
            setError('');
            try {
                await deleteDoc(doc(db, `analyses/${userId}/history`, analysisId));
                if (analysisResult && analysisResult.id === analysisId) {
                    setAnalysisResult(null);
                    setImagePreviewUrl('');
                    setSelectedImage(null);
                    setCompletedWorkouts({});
                    setAudioUrl(null); // Clear audio
                }
                setLoadingMessage('Analysis deleted!');
            } catch (deleteError) {
                console.error("Error deleting analysis:", deleteError);
                setError("Failed to delete analysis. Please try again.");
            } finally {
                setLoading(false);
                setTimeout(() => setLoadingMessage(''), 1000);
            }
        }
    };

    // --- AI Voice Coach Functionality ---
    const handleGenerateVoiceMessage = async () => {
        if (!analysisResult) {
            setError('Please run an analysis first to generate a voice message.');
            return;
        }
        setIsGeneratingAudio(true);
        setLoadingMessage('Generating voice message...');
        setAudioUrl(null);
        setError('');

        try {
            const voiceMessagePrompt = `Generate a short motivational message. The user's body type is: ${analysisResult.bodyType}. The tone should be: ${aiTone}. The message should be positive and encouraging, about a 30-45 second read.`;

            const payload = {
                contents: [{ parts: [{ text: voiceMessagePrompt }] }],
                generationConfig: {
                    responseModalities: ["AUDIO"],
                    speechConfig: {
                        voiceConfig: {
                            prebuiltVoiceConfig: { voiceName: "Kore" }
                        }
                    }
                },
            };

            const googleApiKey = "AIzaSyChwqbh51eMAAak_nWG5nGs3q0CE3jIA3g";
            const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-tts:generateContent?key=${googleApiKey}`;

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(`API Error: ${response.status} - ${errorData.error.message || response.statusText}`);
            }

            const result = await response.json();
            const part = result?.candidates?.[0]?.content?.parts?.[0];
            const audioData = part?.inlineData?.data;
            const mimeType = part?.inlineData?.mimeType;

            if (audioData && mimeType && mimeType.startsWith("audio/")) {
                const sampleRateMatch = mimeType.match(/rate=(\d+)/);
                const sampleRate = sampleRateMatch ? parseInt(sampleRateMatch[1], 10) : 16000;
                const pcmData = base64ToArrayBuffer(audioData);
                const pcm16 = new Int16Array(pcmData);
                const wavBlob = pcmToWav(pcm16, sampleRate);
                const url = URL.createObjectURL(wavBlob);
                setAudioUrl(url);
                setLoadingMessage('Voice message ready!');
            } else {
                setError('Failed to generate audio. Unexpected API response structure.');
                console.error('Unexpected audio response:', result);
            }

        } catch (ttsError) {
            console.error("Error generating voice message:", ttsError);
            setError(`Failed to generate voice message: ${ttsError.message}. Please try again.`);
        } finally {
            setIsGeneratingAudio(false);
            if (!error) {
                setTimeout(() => setLoadingMessage(''), 2000);
            }
        }
    };
    
    // --- Chatbot Logic ---
    const handleChatToggle = () => {
        setIsChatOpen(!isChatOpen);
    };

    const handleChatSubmit = async (e) => {
        e.preventDefault();
        if (!chatInput.trim() || isChatLoading) return;

        const userMessage = chatInput;
        setChatMessages(prev => [...prev, { role: 'user', text: userMessage }]);
        setChatInput('');
        setIsChatLoading(true);

        try {
            const prompt = `You are a helpful AI chatbot focused exclusively on health, fitness, and wellness topics. Keep your answers concise and positive. Answer the following question: ${userMessage}`;
            const payload = {
                contents: [{ role: "user", parts: [{ text: prompt }] }],
            };

            const googleApiKey = "AIzaSyChwqbh51eMAAak_nWG5nGs3q0CE3jIA3g";
            const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-05-20:generateContent?key=${googleApiKey}`;
            
            const apiCall = () => fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const response = await callApiWithBackoff(apiCall);
            const result = await response.json();

            const botResponse = result.candidates?.[0]?.content?.parts?.[0]?.text || 'Sorry, I could not generate a response. Please try again.';
            setChatMessages(prev => [...prev, { role: 'bot', text: botResponse }]);

        } catch (chatError) {
            console.error("Chatbot API error:", chatError);
            setChatMessages(prev => [...prev, { role: 'bot', text: 'Oops! Something went wrong. Please try again later.' }]);
        } finally {
            setIsChatLoading(false);
        }
    };


    return (
        <div className="app-container">
            <div className="main-card">
                {loading && (
                    <div className="loading-overlay">
                        <svg className="loading-overlay-spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <p className="loading-overlay-message">{loadingMessage}</p>
                    </div>
                )}

                <h1 className="main-title">
                    AI Body Analyzer
                </h1>
                <p className="subtitle">
                    Upload your photo and get a personalized body analysis, diet tips, and a 2-week workout plan!
                </p>
                
                {/* User Input Form */}
                <div className="input-form-container">
                    <h3 className="section-title" style={{ marginTop: '0', marginBottom: '1.5rem', fontSize: '1.5rem' }}>Your Information</h3>
                    <div className="input-form-grid">
                        <div className="form-group">
                            <label htmlFor="age" className="form-label">Age</label>
                            <input
                                id="age"
                                type="number"
                                value={age}
                                onChange={(e) => setAge(e.target.value)}
                                className="form-input"
                                placeholder="e.g., 30"
                                disabled={loading}
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="gender" className="form-label">Gender</label>
                            <select
                                id="gender"
                                value={gender}
                                onChange={(e) => setGender(e.target.value)}
                                className="form-select"
                                disabled={loading}
                            >
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                                <option value="other">Other</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label htmlFor="weight" className="form-label">Weight (kg)</label>
                            <input
                                id="weight"
                                type="number"
                                value={weight}
                                onChange={(e) => setWeight(e.target.value)}
                                className="form-input"
                                placeholder="e.g., 75"
                                disabled={loading}
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="height" className="form-label">Height</label>
                            <div className="height-input-group">
                                <input
                                    id="height"
                                    type="number"
                                    value={height}
                                    onChange={(e) => setHeight(e.target.value)}
                                    className="form-input"
                                    placeholder="e.g., 175"
                                    disabled={loading}
                                />
                                <select
                                    value={heightUnit}
                                    onChange={(e) => setHeightUnit(e.target.value)}
                                    className="form-select"
                                    disabled={loading}
                                >
                                    <option value="cm">cm</option>
                                    <option value="m">m</option>
                                    <option value="feet">ft</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* AI Tone Selector */}
                <div className="ai-tone-selector-container">
                    <label htmlFor="aiTone" className="ai-tone-selector-label">
                        AI Analysis Tone:
                    </label>
                    <select
                        id="aiTone"
                        className="ai-tone-selector"
                        value={aiTone}
                        onChange={(e) => setAiTone(e.target.value)}
                        disabled={loading}
                    >
                        <option value="Motivational">Motivational</option>
                        <option value="Factual">Factual</option>
                        <option value="Concise">Concise</option>
                    </select>
                </div>

                {/* Image Upload Section */}
                <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`upload-area ${isDragging ? 'dragging' : ''}`}
                >
                    {!imagePreviewUrl ? (
                        <>
                            <UploadCloud className={`upload-icon ${isDragging ? 'active' : ''}`} />
                            <p className={`upload-text ${isDragging ? 'dragging' : ''}`}>
                                {isDragging ? "Drop your image here!" : "Drag & Drop your image here"}
                            </p>
                            <p className="upload-or">or</p>
                            <label
                                htmlFor="file-upload"
                                className="browse-button"
                            >
                                Browse Files
                            </label>
                            <input
                                id="file-upload"
                                type="file"
                                accept="image/*"
                                onChange={handleFileInputChange}
                                className="file-input"
                            />
                        </>
                    ) : (
                        <div className="image-preview-container">
                            <img
                                src={imagePreviewUrl}
                                alt="Preview"
                                className="image-preview"
                            />
                            <button
                                onClick={handleRemoveImage}
                                className="remove-image-button"
                                aria-label="Remove image"
                            >
                                <XCircle size={24} />
                            </button>
                        </div>
                    )}
                </div>

                <button
                    onClick={handleAnalyzeBody}
                    disabled={!selectedImage || loading || !userId || !weight || !height || !age || !gender}
                    className="analyze-button"
                >
                    {loading ? (
                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg className="loading-spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            {loadingMessage || 'Analyzing...'}
                        </span>
                    ) : (
                        'Analyze My Body'
                    )}
                </button>

                {/* User ID Display */}
                {userId && (
                    <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', marginTop: '1rem' }}>
                        User ID: {userId.substring(0, 8)}... (for data persistence)
                    </p>
                )}


                {error && (
                    <div className="error-message">
                        <AlertCircle className="error-icon" size={24} />
                        <div>
                            <p className="font-semibold">Error:</p>
                            <p>{error}</p>
                        </div>
                    </div>
                )}

                {/* Analysis History Section */}
                {savedAnalyses.length > 0 && (
                    <section className="analysis-history-section">
                        <h3 className="analysis-history-title">
                            <History size={32} style={{ marginRight: '1rem', color: 'var(--gray-700)' }} /> Your Analysis History
                        </h3>
                        <ul className="analysis-history-list">
                            {savedAnalyses.map((analysis) => (
                                <li key={analysis.id} className="analysis-history-item">
                                    <div className="analysis-item-info">
                                        {analysis.imageData && analysis.imageMimeType && (
                                            <img
                                                src={`data:${analysis.imageMimeType};base64,${analysis.imageData}`}
                                                alt="Analysis thumbnail"
                                                className="analysis-history-thumbnail"
                                            />
                                        )}
                                        <div className="analysis-item-text-content">
                                            <span>{new Date(analysis.timestamp?.toDate()).toLocaleString()}</span>
                                            <span>Body Type: {analysis.bodyType}</span>
                                        </div>
                                    </div>
                                    <div className="analysis-item-buttons">
                                        <button
                                            onClick={() => handleLoadSpecificAnalysis(analysis.id)}
                                            className="analysis-item-button load-item"
                                            title="Load this analysis"
                                        >
                                            <FolderOpen size={20} />
                                        </button>
                                        <button
                                            onClick={() => handleOpenSchedule(analysis)}
                                            className="analysis-item-button schedule-item"
                                            title="View weekly schedule"
                                        >
                                            <CalendarDays size={20} />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteAnalysis(analysis.id)}
                                            className="analysis-item-button delete-item"
                                            title="Delete this analysis"
                                        >
                                            <Trash2 size={20} />
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                {analysisResult && (
                    <div className="analysis-results">
                        {/* AI Voice Coach Section */}
                        <div className="voice-coach-container">
                            <h3>Your AI Voice Coach</h3>
                            {!audioUrl ? (
                                <button
                                    onClick={handleGenerateVoiceMessage}
                                    disabled={isGeneratingAudio}
                                    className="voice-coach-button"
                                >
                                    {isGeneratingAudio ? (
                                        <>
                                            <svg className="loading-spinner" style={{ color: 'white', marginRight: '0.5rem', height: '1.25rem', width: '1.25rem' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Generating...
                                        </>
                                    ) : (
                                        <>
                                            <Volume2 size={20} /> Generate Motivational Voice Message
                                        </>
                                    )}
                                </button>
                            ) : (
                                <div className="audio-player-container">
                                    <audio controls autoPlay src={audioUrl}></audio>
                                </div>
                            )}
                        </div>
                        
                        {/* Health Insights Section */}
                        <section className="section-card health-risk-section">
                            <div className="health-risk-header">
                                <HeartPulse size={40} style={{ color: '#00796b' }} />
                                <h3 className="section-title" style={{ color: '#004d40' }}>Health Insights</h3>
                            </div>
                            <div className="health-insights-content">
                                <div className="health-insights-left">
                                    <div className="insight-card">
                                        <p>Estimated BMI</p>
                                        <strong className="main-value">{analysisResult.estimatedBMI}</strong>
                                    </div>
                                    <div className="insight-card">
                                        <p>Age & Demographic Analysis</p>
                                        <strong>{analysisResult.ageGenderComparison}</strong>
                                    </div>
                                </div>
                                <div className="health-insights-right">
                                    <div className="insight-card-full">
                                        <p>Health Risk Assessment</p>
                                        <strong>{analysisResult.healthRiskAssessment}</strong>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Body Type Section */}
                        <section className="section-card body-type-section">
                            <PersonStanding className="section-icon" />
                            <div>
                                <h3 className="section-title">Your Body Type</h3>
                                <p className="section-content">{analysisResult.bodyType}</p>
                            </div>
                        </section>

                        {/* Fat Distribution Section */}
                        <section className="section-card fat-distribution-section">
                            <Target className="section-icon" />
                            <div>
                                <h3 className="section-title">Fat Distribution Areas</h3>
                                {analysisResult.fatDistributionAreas && analysisResult.fatDistributionAreas.length > 0 ? (
                                    <ul className="section-content" style={{ listStyle: 'disc', paddingLeft: '1.25rem' }}>
                                        {analysisResult.fatDistributionAreas.map((area, index) => (
                                            <li key={index}>{area}</li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="section-content">No specific prominent fat distribution areas identified.</p>
                                )}
                            </div>
                        </section>

                        {/* Healthy Diet Suggestions Section */}
                        <section className="section-card diet-suggestions-section">
                            <Salad className="section-icon" />
                            <div>
                                <h3 className="section-title">Healthy Diet Suggestions</h3>
                                <p className="section-content" style={{ whiteSpace: 'pre-wrap' }}>{analysisResult.healthyDietSuggestions}</p>
                            </div>
                        </section>

                        {/* 2-Week Daily Workout Plan Section */}
                        <section className="section-card workout-plan-section">
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1.5rem' }}>
                                <Dumbbell className="section-icon" />
                                <h3 className="section-title">2-Week Daily Workout Plan</h3>
                            </div>
                            <div className="workout-table-container">
                                <table className="workout-table">
                                    <thead>
                                        <tr>
                                            <th>Week</th>
                                            <th>Day</th>
                                            <th>Focus</th>
                                            <th>Exercises</th>
                                            <th>Completed</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {analysisResult.twoWeekWorkoutPlan && analysisResult.twoWeekWorkoutPlan.map((plan, index) => {
                                            const workoutKey = `week-${plan.week}-day-${index}`;
                                            const isCompleted = completedWorkouts[workoutKey];
                                            return (
                                                <tr key={index} className={`workout-row ${isCompleted ? 'completed' : ''}`}>
                                                    <td>{plan.week}</td>
                                                    <td>{plan.day}</td>
                                                    <td>{plan.focus}</td>
                                                    <td>
                                                        <ul className="workout-table-exercises">
                                                            {plan.exercises.map((exercise, exIndex) => (
                                                                <li key={exIndex}>{exercise}</li>
                                                            ))}
                                                        </ul>
                                                    </td>
                                                    <td>
                                                        <div className="workout-checkbox-container">
                                                            <input
                                                                type="checkbox"
                                                                id={`workout-${workoutKey}`}
                                                                className="workout-checkbox"
                                                                checked={isCompleted}
                                                                onChange={() => handleToggleWorkoutCompletion(plan.week, index)}
                                                            />
                                                            <label htmlFor={`workout-${workoutKey}`} className="workout-checkbox-label">
                                                                Done
                                                            </label>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </section>

                        {/* Disclaimer */}
                        <div className="disclaimer-section">
                            <AlertCircle className="disclaimer-icon" />
                            <div>
                                <p className="font-semibold">Disclaimer:</p>
                                <p className="text-sm">This analysis is based on visual information and provides general suggestions. For personalized health, fitness, or dietary advice, please consult a qualified professional. Do not use this information for self-diagnosis or to replace professional medical advice.</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Chatbot UI */}
            <button className="chatbot-fab" onClick={handleChatToggle}>
                <MessageCircle size={32} />
            </button>

            {isChatOpen && (
                <div className="chatbot-window">
                    <div className="chatbot-header">
                        <h3>AI Health Coach</h3>
                        <button onClick={handleChatToggle}>
                            <XCircle size={24} />
                        </button>
                    </div>
                    <div className="chatbot-messages">
                        {chatMessages.map((msg, index) => (
                            <div key={index} className={`chat-message ${msg.role}`}>
                                <div className={`chat-bubble ${msg.role}`}>
                                    {msg.text}
                                </div>
                            </div>
                        ))}
                        {isChatLoading && (
                            <div className="chat-message bot">
                                <div className="chat-bubble bot">
                                    <svg className="loading-spinner" style={{ color: 'black', height: '1.25rem', width: '1.25rem' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                    <form className="chatbot-input-area" onSubmit={handleChatSubmit}>
                        <input
                            type="text"
                            placeholder="Ask a question..."
                            value={chatInput}
                            onChange={(e) => setChatInput(e.target.value)}
                            disabled={isChatLoading}
                        />
                        <button type="submit" disabled={isChatLoading}>
                            <Send size={20} />
                        </button>
                    </form>
                </div>
            )}

            {/* Schedule Modal */}
            {isScheduleOpen && (
                <div className="modal-overlay">
                    <div className="schedule-modal">
                        <div className="schedule-modal-header">
                            <h3>Weekly Plan</h3>
                            <button className="schedule-modal-close-button" onClick={handleCloseSchedule}>
                                <X size={24} />
                            </button>
                        </div>
                        
                        {/* Timetable Grid */}
                        <div className="timetable-grid">
                            {scheduleAnalysis.twoWeekWorkoutPlan.slice(0, 7).map((dayPlan, index) => (
                                <div
                                    key={index}
                                    className={`day-box ${index === selectedDayIndex ? 'active' : ''}`}
                                    onClick={() => setSelectedDayIndex(index)}
                                >
                                    <h4>{dayPlan.day}</h4>
                                    <span>{dayPlan.focus}</span>
                                </div>
                            ))}
                        </div>

                        {/* Day Details */}
                        <div className="day-details">
                            {scheduleAnalysis.twoWeekWorkoutPlan[selectedDayIndex] && (
                                <>
                                    <h4>{scheduleAnalysis.twoWeekWorkoutPlan[selectedDayIndex].day} Details</h4>
                                    <h5 style={{ fontWeight: 600, marginTop: '1rem', marginBottom: '0.5rem' }}>Exercises:</h5>
                                    <ul style={{ listStyle: 'disc', paddingLeft: '1.25rem' }}>
                                        {scheduleAnalysis.twoWeekWorkoutPlan[selectedDayIndex].exercises.map((exercise, i) => (
                                            <li key={i}>{exercise}</li>
                                        ))}
                                    </ul>
                                    <h5 style={{ fontWeight: 600, marginTop: '1rem', marginBottom: '0.5rem' }}>Diet Suggestions:</h5>
                                    <p style={{ whiteSpace: 'pre-wrap' }}>{scheduleAnalysis.healthyDietSuggestions}</p>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default App;
